import React, { useState, useEffect, useMemo, memo } from 'react';
import { Trophy, TrendingUp, Award, Target, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { NativeSelect } from './ui/native-select';
import api, { dashboardAPI, objetivosAPI, vendasAPI } from '../services/api';
import SaudeSaldosCard from './SaudeSaldosCard';

// URL centralizado que funciona em produção mesmo com .env errado
import { getBackendUrl } from '../utils/config';
const API_URL = getBackendUrl();

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

// Componente de KPI memoizado para evitar re-renders desnecessários
const KPICard = memo(({ title, value, subtitle, icon: Icon, iconColor }) => (
  <Card className="hover:shadow-elegant transition-all">
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-3xl font-bold" style={{ color: iconColor }}>{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <Icon className="h-8 w-8" style={{ color: iconColor }} />
      </div>
    </CardContent>
  </Card>
));

// Agregar vendas por secção (remove duplicados somando valores da mesma secção)
const agregarVendasPorSeccao = (vendas) => {
  if (!vendas || vendas.length === 0) return [];
  const mapa = {};
  vendas.forEach(v => {
    const key = v.seccao;
    if (!mapa[key]) mapa[key] = 0;
    mapa[key] += v.valor;
  });
  return Object.entries(mapa)
    .map(([seccao, valor]) => ({ seccao, valor }))
    .sort((a, b) => b.valor - a.valor);
};

// Componente de Ranking Item — estilo card (imagem de referência)
const RankingItem = memo(({ item }) => {
  const vendas = agregarVendasPorSeccao(item.vendas_por_seccao);
  const isFirst = item.posicao === 1;
  const isTop3 = item.posicao <= 3;

  return (
    <div
      data-testid={`ranking-item-${item.posicao}`}
      className={`relative rounded-xl border px-4 py-3 transition-all ${
        isFirst
          ? 'bg-amber-50 border-amber-300 ring-1 ring-amber-200'
          : isTop3
            ? 'bg-[#f0fafa] border-[#d0eded]'
            : 'bg-white border-gray-200'
      }`}
    >
      {isFirst && (
        <span className="absolute -top-2 -right-2 bg-amber-500 text-white text-[10px] font-black px-1.5 py-0.5 rounded-full shadow-sm">#1</span>
      )}
      <div className="flex items-start gap-3">
        {/* Posição */}
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 mt-0.5 ${
            isFirst
              ? 'bg-amber-500 text-white'
              : isTop3
                ? 'bg-amber-400 text-white'
                : 'bg-gray-200 text-gray-600'
          }`}
        >
          {item.posicao}
        </div>

        {/* Info central */}
        <div className="flex-1 min-w-0">
          <p className={`font-bold uppercase tracking-wide text-sm ${isFirst ? 'text-amber-900' : 'text-gray-900'}`}>
            {item.colaborador_nome}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {'\u20AC'}{(item.total_vendas || 0).toFixed(2)} em vendas
          </p>
          {vendas.length > 0 && (
            <div className="text-xs text-gray-400 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
              {vendas.map((v, i) => (
                <span key={i}>
                  <span className="font-medium text-gray-500">{v.seccao}:</span>{' '}
                  {'\u20AC'}{v.valor.toFixed(2)}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Badge de pontos */}
        <span
          data-testid={`ranking-pts-${item.posicao}`}
          className="shrink-0 inline-flex items-center rounded-full px-3 py-1 text-sm font-bold text-white"
          style={{ backgroundColor: '#38bec9' }}
        >
          {item.pontos} pts
        </span>
      </div>
    </div>
  );
});

// Mapeamento de secções (constante global do componente)
const MAPEAMENTO_SECOES = {
  'cafe': 'Café',
  'loja_avelal': 'Loja Avelal',
  'loja_satao': 'Loja Sátão',
  'escritorio': 'Escritório',
  'raspadinhas': 'Raspadinhas',
  'restaurante': 'Restaurante',
  'jogos': 'Jogos'
};

// Dashboard atualizado - v2.2 (com melhor tratamento de erros)
const DashboardTab = () => {
  // Estado por defeito para evitar erros quando API falha
  const defaultStats = {
    mes_atual: new Date().getMonth() + 1,
    ano_atual: new Date().getFullYear(),
    colaborador_id: '',
    vendas_por_seccao: [],
    minhas_vendas_por_seccao: [],
    pontos_mes: 0,
    ranking_mensal: [],
    posicao_ranking: 0,
    vendas_anuais_por_seccao: {},
    minhas_vendas_anuais_por_seccao: {},
    minhas_pontos_anuais_por_seccao: {}
  };
  
  const [stats, setStats] = useState(defaultStats);
  const [rankingAnual, setRankingAnual] = useState([]);
  const [objetivos, setObjetivos] = useState([]);
  const [objetivosAgrupados, setObjetivosAgrupados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mesSelecionado, setMesSelecionado] = useState(new Date().getMonth() + 1);
  const [anoSelecionado, setAnoSelecionado] = useState(new Date().getFullYear());
  const [paginaRankingMensal, setPaginaRankingMensal] = useState(1);
  const [paginaRankingAnual, setPaginaRankingAnual] = useState(1);
  const [regrasPontos, setRegrasPontos] = useState([]);
  const ITENS_POR_PAGINA = 10;

  useEffect(() => {
    loadData();
  }, [mesSelecionado, anoSelecionado]);

  const loadData = async () => {
    console.log('🚀 [DASHBOARD] Iniciando loadData - v3');
    setError(null);
    try {
      // Carregar vendas do ano inteiro para calcular progresso anual
      // Usar Promise.allSettled para não falhar tudo se uma API falhar
      const results = await Promise.allSettled([
        dashboardAPI.getStats({ mes: mesSelecionado, ano: anoSelecionado }),
        dashboardAPI.getRankingAnual({ ano: anoSelecionado }),
        objetivosAPI.get(`/objetivos-vendas/${anoSelecionado}`),
        objetivosAPI.get(`/objetivos-vendas-mensal/${anoSelecionado}/${mesSelecionado}`),
        vendasAPI.getAll({ ano: anoSelecionado }),
        api.get(`/pontos-turno/regras/${anoSelecionado}/${mesSelecionado}`)
      ]);
      
      // Extrair resultados, usando valores por defeito em caso de erro
      const [statsRes, rankingAnualRes, objetivosAnuaisRes, objetivosMensaisRes, vendasAnuaisRes, regrasPontosRes] = results.map((result, index) => {
        if (result.status === 'fulfilled') {
          return result.value;
        } else {
          console.error(`❌ Erro na API ${index}:`, result.reason);
          return { data: index === 0 ? defaultStats : (index === 1 ? [] : null) };
        }
      });

      // Usar dados ou valores por defeito
      setStats(statsRes?.data || defaultStats);
      setRankingAnual(rankingAnualRes?.data || []);
      console.log('🎯 Regras de pontos carregadas:', regrasPontosRes.data);
      setRegrasPontos(regrasPontosRes.data || []);
      
      // Transformar objetivos de vendas em formato esperado pelo Dashboard
      const objetivosTransformados = [];
      const gruposTransformados = [];
      
      // Processar objetivos anuais
      
      if (objetivosAnuaisRes.data && objetivosAnuaisRes.data.secoes && typeof objetivosAnuaisRes.data.secoes === 'object') {
        
        // ORDEM FIXA para apresentação (sempre a mesma)
        const ordemSeccoes = ['cafe', 'raspadinhas', 'loja_avelal', 'loja_satao', 'restaurante', 'escritorio', 'jogos'];
        
        ordemSeccoes.forEach(secaoKey => {
          const dados = objetivosAnuaisRes.data.secoes[secaoKey];
          if (!dados) return;
          
          if (dados.anual && dados.anual > 0) {
            const objetivo = {
              id: `anual-${secaoKey}`,
              tipo: 'anual',
              seccao: MAPEAMENTO_SECOES[secaoKey] || secaoKey,
              valor: dados.anual,
              mes: null
            };
            console.log('✅ Objetivo anual adicionado:', objetivo);
            objetivosTransformados.push(objetivo);
          } else {
            console.log('❌ Objetivo anual ignorado (valor inválido):', dados);
          }
        });
        
        // Processar grupos anuais
        if (objetivosAnuaisRes.data.grupos && Array.isArray(objetivosAnuaisRes.data.grupos)) {
          objetivosAnuaisRes.data.grupos.forEach(grupo => {
            const grupoTransformado = {
              ...grupo,
              tipo: 'anual',
              secoes: grupo.secoes || []
            };
            gruposTransformados.push(grupoTransformado);
          });
        }
      }
      
      // Processar objetivos mensais
      if (objetivosMensaisRes.data && objetivosMensaisRes.data.secoes && typeof objetivosMensaisRes.data.secoes === 'object') {
        // ORDEM FIXA para apresentação (sempre a mesma)
        const ordemSeccoes = ['cafe', 'raspadinhas', 'loja_avelal', 'loja_satao', 'restaurante', 'escritorio', 'jogos'];
        
        // Definir secções visíveis baseado no perfil
        // Obter papel do localStorage
        let userRole = 'colaborador';
        try {
          const userFromStorage = localStorage.getItem('user');
          if (userFromStorage) {
            const parsedUser = JSON.parse(userFromStorage);
            userRole = parsedUser.role || 'colaborador';
          }
        } catch (e) {
          console.error('Erro ao buscar user role:', e);
        }
        
        let seccoesVisiveis;
        
        if (userRole === 'admin') {
          // Admin vê TODAS as secções
          seccoesVisiveis = ['cafe', 'raspadinhas', 'loja_avelal', 'loja_satao', 'restaurante', 'jogos', 'escritorio'];
        } else {
          // Colaboradores vêem apenas estas 5 secções
          seccoesVisiveis = ['cafe', 'raspadinhas', 'loja_avelal', 'loja_satao', 'restaurante'];
        }
        
        // Processar na ordem definida
        ordemSeccoes.forEach(secaoKey => {
          if (!objetivosMensaisRes.data.secoes[secaoKey]) return;
          
          const dados = objetivosMensaisRes.data.secoes[secaoKey];
          
          // Filtrar baseado no perfil E mostrar mesmo com 0€
          if (seccoesVisiveis.includes(secaoKey) && dados.mensal !== undefined) {
            objetivosTransformados.push({
              id: `mensal-${mesSelecionado}-${secaoKey}`,
              tipo: 'mensal',
              seccao: MAPEAMENTO_SECOES[secaoKey] || secaoKey,
              valor: dados.mensal || 0,  // Mostrar mesmo com 0€
              mes: mesSelecionado
            });
          }
        });
        
        // Processar grupos mensais
        if (objetivosMensaisRes.data.grupos && Array.isArray(objetivosMensaisRes.data.grupos)) {
          objetivosMensaisRes.data.grupos.forEach(grupo => {
            console.log('  - Grupo mensal:', grupo.nome, 'Seções:', grupo.secoes);
            gruposTransformados.push({
              ...grupo,
              tipo: 'mensal',
              secoes: grupo.secoes || []
            });
          });
        }
      }
      
      console.log('📊 [FINAL] Total objetivos:', objetivosTransformados.length);
      console.log('📊 [FINAL] Grupos transformados:', gruposTransformados);
      console.log('📊 [FINAL] Grupos têm secoes?', gruposTransformados.map(g => ({nome: g.nome, secoes: g.secoes})));
      setObjetivos(objetivosTransformados);
      setObjetivosAgrupados(gruposTransformados);
      
      // Calcular vendas mensais e anuais por secção (EQUIPA e COLABORADOR)
      const vendasPorSeccaoAnual = {};
      const vendasPorSeccaoMensal = {};
      const minhasVendasAnuais = {};
      const minhasVendasMensais = {};
      
      if (vendasAnuaisRes.data && Array.isArray(vendasAnuaisRes.data)) {
        vendasAnuaisRes.data.forEach(venda => {
        // Vendas anuais da equipa
        if (!vendasPorSeccaoAnual[venda.seccao]) {
          vendasPorSeccaoAnual[venda.seccao] = 0;
        }
        vendasPorSeccaoAnual[venda.seccao] += venda.valor;
        
        // Vendas mensais da equipa (apenas do mês selecionado)
        if (venda.data && venda.data.startsWith(`${anoSelecionado}-${String(mesSelecionado).padStart(2, '0')}`)) {
          if (!vendasPorSeccaoMensal[venda.seccao]) {
            vendasPorSeccaoMensal[venda.seccao] = { total: 0 };
          }
          vendasPorSeccaoMensal[venda.seccao].total += venda.valor;
        }
        
        // Vendas do colaborador individual (anual)
        if (venda.colaborador_id === statsRes.data.colaborador_id) {
          if (!minhasVendasAnuais[venda.seccao]) {
            minhasVendasAnuais[venda.seccao] = 0;
          }
          minhasVendasAnuais[venda.seccao] += venda.valor;
          
          // Vendas do colaborador individual (mensal)
          if (venda.data && venda.data.startsWith(`${anoSelecionado}-${String(mesSelecionado).padStart(2, '0')}`)) {
            if (!minhasVendasMensais[venda.seccao]) {
              minhasVendasMensais[venda.seccao] = { total: 0 };
            }
            minhasVendasMensais[venda.seccao].total += venda.valor;
          }
        }
        });
      }
      
      // Converter para array no formato esperado
      const minhasVendasPorSeccaoList = Object.keys(minhasVendasMensais).map(seccao => ({
        seccao,
        total: minhasVendasMensais[seccao].total
      }));
      
      // Usar vendas do backend (já inclui monitorização, fechos, vendas diretas)
      // Apenas adicionar "minhas_vendas" calculadas do frontend
      setStats(prev => ({
        ...prev,
        // NÃO sobrescrever vendas_por_seccao - vem do backend já calculado!
        // vendas_por_seccao: já vem do backend com dados corretos
        minhas_vendas_anuais_por_seccao: minhasVendasAnuais
      }));
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  // Memoizar cálculos pesados (sempre antes dos returns condicionais)
  const totalVendas = useMemo(() => 
    stats?.vendas_por_seccao ? stats.vendas_por_seccao.reduce((sum, s) => sum + s.total, 0) : 0,
    [stats?.vendas_por_seccao]
  );
  
  // Calcular pontos anuais
  const pontosAnuais = useMemo(() => 
    rankingAnual.find(r => r.colaborador_id === stats?.colaborador_id)?.pontos || 0,
    [rankingAnual, stats?.colaborador_id]
  );
  
  const posicaoAnual = useMemo(() => 
    rankingAnual.findIndex(r => r.colaborador_id === stats?.colaborador_id) + 1,
    [rankingAnual, stats?.colaborador_id]
  );
  
  // Filtrar objetivos
  const objetivosMensais = useMemo(() => 
    objetivos.filter(o => o.tipo === 'mensal' && o.mes === mesSelecionado),
    [objetivos, mesSelecionado]
  );
  
  const objetivosAnuais = useMemo(() => {
    const filtered = objetivos.filter(o => o.tipo === 'anual');
    console.log('🔍 DASHBOARD - Total objetivos:', objetivos.length);
    console.log('🔍 DASHBOARD - Objetivos anuais filtrados:', filtered.length, filtered);
    return filtered;
  }, [objetivos]);

  if (loading) return (
    <div className="space-y-4 p-4">
      {/* Widget renderizado mesmo durante loading do Dashboard */}
      <SaudeSaldosCard />
      <div className="flex items-center justify-center p-8">
        <div className="animate-pulse text-muted-foreground">Carregando...</div>
      </div>
    </div>
  );

  // Mostrar erro se houver, mas continuar com dados parciais
  if (error && !stats?.colaborador_id) {
    return (
      <div className="space-y-4 p-4">
        <SaudeSaldosCard />
        <div className="flex flex-col items-center justify-center p-8 gap-4">
          <div className="text-destructive text-center">
            <p className="font-semibold">Erro ao carregar dados</p>
            <p className="text-sm text-muted-foreground mt-2">{error}</p>
          </div>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  // DEBUG: Verificar objetivos disponíveis

  return (
    <div className="space-y-6">
      {/* Widget de Saúde dos Saldos/Fidelização — apenas para admin (auto-hide via 403) */}
      <SaudeSaldosCard />

      {/* Filtros */}
      <Card className="bg-gradient-card border-primary/20">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-primary" />
              <span className="font-semibold">Período:</span>
            </div>
            <NativeSelect
              value={mesSelecionado}
              onChange={(e) => setMesSelecionado(parseInt(e.target.value))}
              className="w-40"
            >
              {MESES.map((mes, idx) => (
                <option key={idx} value={idx + 1}>{mes}</option>
              ))}
            </NativeSelect>
            <NativeSelect
              value={anoSelecionado}
              onChange={(e) => setAnoSelecionado(parseInt(e.target.value))}
              className="w-32"
            >
              <option value={2024}>2024</option>
              <option value={2025}>2025</option>
              <option value={2026}>2026</option>
            </NativeSelect>
          </div>
        </CardContent>
      </Card>

      {/* Alerta quando não há dados para o ano selecionado */}
      {anoSelecionado === 2025 && stats.vendas_por_seccao?.length === 0 && stats.minhas_vendas_anuais_por_seccao && Object.keys(stats.minhas_vendas_anuais_por_seccao).length === 0 && (
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="text-yellow-600">⚠️</div>
              <div>
                <h3 className="font-semibold text-yellow-800 mb-1">Sem vendas registradas em {anoSelecionado}</h3>
                <p className="text-sm text-yellow-700 mb-2">
                  Não foram encontradas vendas para o ano atual. Os dados históricos estão disponíveis em anos anteriores.
                </p>
                <button
                  onClick={() => setAnoSelecionado(2024)}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm font-medium transition-colors"
                >
                  Ver dados de 2024
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 1. Pontos por Turno — Como Ganhar Pontos */}
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-700">
            <Award className="h-5 w-5" />
            Pontos por Turno - {MESES[mesSelecionado - 1]} {anoSelecionado}
          </CardTitle>
          <p className="text-sm text-blue-600 mt-1">
            Valores que precisa vender em cada secção para ganhar pontos
          </p>
        </CardHeader>
        <CardContent>
          {regrasPontos && regrasPontos.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {(regrasPontos || [])
                .sort((a, b) => {
                  const ordem = ['Cafe', 'Raspadinhas', 'Loja Avelal', 'Loja Satao'];
                  const indexA = ordem.indexOf(a.seccao);
                  const indexB = ordem.indexOf(b.seccao);
                  return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
                })
                .map((regra, idx) => (
                  <div
                    key={idx}
                    data-testid={`regra-pontos-${idx}`}
                    className="bg-white rounded-lg p-4 border border-blue-100 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <h3 className="font-bold text-lg mb-3 text-blue-700 flex items-center gap-2">
                      <span className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-sm">
                        {regra.seccao?.[0] || '?'}
                      </span>
                      {regra.seccao || 'Secção'}
                    </h3>
                    <div className="space-y-2">
                      {regra.pontos_percentagens && Object.entries(regra.pontos_percentagens)
                        .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
                        .map(([pontos, info]) => {
                          const valorPorTurno = info.valor_por_turno || 0;
                          return (
                            <div
                              key={pontos}
                              className="flex items-center justify-between p-2 bg-gradient-to-r from-green-50 to-emerald-50 rounded border border-green-200"
                            >
                              <Badge variant="default" className="bg-green-600">
                                {pontos}pt{parseInt(pontos) > 1 ? 's' : ''}
                              </Badge>
                              <div className="text-right">
                                <p className="font-bold text-green-700">
                                  {valorPorTurno.toFixed(2)}€
                                </p>
                                <p className="text-xs text-green-600">por turno</p>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                    {regra.objetivo_mensal && (
                      <div className="mt-3 pt-3 border-t border-blue-100">
                        <p className="text-xs text-blue-600">
                          Objetivo Mensal: <span className="font-bold">{regra.objetivo_mensal.toFixed(2)}€</span>
                        </p>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          ) : (
            <div className="text-center py-8 px-4">
              <Award className="h-16 w-16 mx-auto text-blue-300 mb-4" />
              <p className="text-lg font-semibold text-blue-700 mb-2">
                Nenhuma regra de pontos configurada
              </p>
              <p className="text-sm text-blue-600">
                As regras de pontos são criadas automaticamente quando o administrador define objectivos na aba Monitorização.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 2. Rankings — Mensal e Anual lado a lado */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ranking Mensal */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-bold" data-testid="ranking-mensal-title">
              Ranking Mensal - {MESES[mesSelecionado - 1]}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(stats?.ranking_mensal || []).length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum dado de ranking ainda
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  {(stats?.ranking_mensal || [])
                    .slice((paginaRankingMensal - 1) * ITENS_POR_PAGINA, paginaRankingMensal * ITENS_POR_PAGINA)
                    .map((item) => (
                      <RankingItem key={item.colaborador_id} item={item} />
                    ))}
                </div>
                {(stats?.ranking_mensal || []).length > ITENS_POR_PAGINA && (
                  <div className="flex justify-center gap-2 mt-4">
                    <button
                      onClick={() => setPaginaRankingMensal(p => Math.max(1, p - 1))}
                      disabled={paginaRankingMensal === 1}
                      className="px-3 py-1 rounded bg-muted disabled:opacity-50 text-sm"
                    >
                      Anterior
                    </button>
                    <span className="px-3 py-1 text-sm">
                      Pág. {paginaRankingMensal}/{Math.ceil((stats?.ranking_mensal || []).length / ITENS_POR_PAGINA)}
                    </span>
                    <button
                      onClick={() => setPaginaRankingMensal(p => Math.min(Math.ceil((stats?.ranking_mensal || []).length / ITENS_POR_PAGINA), p + 1))}
                      disabled={paginaRankingMensal >= Math.ceil((stats?.ranking_mensal || []).length / ITENS_POR_PAGINA)}
                      className="px-3 py-1 rounded bg-muted disabled:opacity-50 text-sm"
                    >
                      Próximo
                    </button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Ranking Anual */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-bold" data-testid="ranking-anual-title">
              Ranking Anual - {anoSelecionado}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {rankingAnual.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum dado de ranking ainda
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  {(rankingAnual || [])
                    .slice((paginaRankingAnual - 1) * ITENS_POR_PAGINA, paginaRankingAnual * ITENS_POR_PAGINA)
                    .map((item) => (
                      <RankingItem key={item.colaborador_id} item={item} />
                    ))}
                </div>
                {(rankingAnual || []).length > ITENS_POR_PAGINA && (
                  <div className="flex justify-center gap-2 mt-4">
                    <button
                      onClick={() => setPaginaRankingAnual(p => Math.max(1, p - 1))}
                      disabled={paginaRankingAnual === 1}
                      className="px-3 py-1 rounded bg-muted disabled:opacity-50 text-sm"
                    >
                      Anterior
                    </button>
                    <span className="px-3 py-1 text-sm">
                      Pág. {paginaRankingAnual}/{Math.ceil(rankingAnual.length / ITENS_POR_PAGINA)}
                    </span>
                    <button
                      onClick={() => setPaginaRankingAnual(p => Math.min(Math.ceil(rankingAnual.length / ITENS_POR_PAGINA), p + 1))}
                      disabled={paginaRankingAnual >= Math.ceil(rankingAnual.length / ITENS_POR_PAGINA)}
                      className="px-3 py-1 rounded bg-muted disabled:opacity-50 text-sm"
                    >
                      Próximo
                    </button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* 4. e 5. e 6. e 7. Objetivos (Mensais e Anuais com seus grupos) */}
      {(objetivosMensais.length > 0 || objetivosAnuais.length > 0) && (
        <Card>
          <CardHeader>
            <CardTitle>Objetivos de Equipa</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Objetivos Agrupados Mensais - PRIMEIRO */}
              {(() => {
                // Filtrar grupos mensais e calcular se têm metas definidas
                const gruposMensaisComMeta = objetivosAgrupados.filter(g => {
                  if (g.tipo !== 'mensal') return false;
                  
                  // Verificar se o grupo tem objetivos com valores > 0
                  let temMeta = false;
                  if (g.secoes && Array.isArray(g.secoes)) {
                    g.secoes.forEach(seccaoNome => {
                      const seccaoKey = seccaoNome.toLowerCase().replace(/ /g, '_').replace(/ã/g, 'a');
                      const seccaoNormalizada = MAPEAMENTO_SECOES[seccaoKey] || seccaoNome;
                      const objetivo = objetivos.find(o => 
                        o.tipo === 'mensal' && 
                        o.seccao === seccaoNormalizada && 
                        o.mes === mesSelecionado
                      );
                      if (objetivo && objetivo.valor > 0) {
                        temMeta = true;
                      }
                    });
                  }
                  return temMeta;
                });
                
                return gruposMensaisComMeta.length > 0;
              })() && (
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Trophy className="h-4 w-4 text-purple-500" />
                    Objetivos Agrupados Mensais - {MESES[mesSelecionado - 1]}
                  </h4>
                  <div className="grid gap-3">
                    {(objetivosAgrupados || []).filter(g => g.tipo === 'mensal').map((grupo) => {
                      let metaTotal = 0;
                      let vendasEquipa = 0;
                      let minhasVendas = 0;
                      
                      // Somar apenas o mês selecionado (mês atual)
                      if (grupo.secoes && Array.isArray(grupo.secoes)) {
                        grupo.secoes.forEach(seccaoNome => {
                        // Normalizar nome da seção
                        const seccaoKey = seccaoNome.toLowerCase().replace(/ /g, '_').replace(/ã/g, 'a');
                        const seccaoNormalizada = MAPEAMENTO_SECOES[seccaoKey] || seccaoNome;
                        
                        console.log(`  - Processando: ${seccaoNome} -> ${seccaoKey} -> ${seccaoNormalizada}`);
                        
                        const objetivo = objetivos.find(o => 
                          o.tipo === 'mensal' && 
                          o.seccao === seccaoNormalizada && 
                          o.mes === mesSelecionado
                        );
                        
                        if (objetivo) {
                          console.log(`  ✅ Objetivo encontrado: ${objetivo.valor}€`);
                          metaTotal += objetivo.valor;
                        } else {
                          console.log(`  ❌ Objetivo NÃO encontrado para: ${seccaoNormalizada}`);
                        }
                        
                        // Calcular vendas da equipa (todas as vendas do mês atual desta secção)
                        const vendaSeccao = stats.vendas_por_seccao.find(v => v.seccao === seccaoNormalizada);
                        if (vendaSeccao) {
                          console.log(`  📊 Vendas equipa ${seccaoNormalizada}: ${vendaSeccao.total}€`);
                          vendasEquipa += vendaSeccao.total;
                        }
                        
                        // Calcular minhas vendas (só do colaborador logado)
                        const minhaVendaSeccao = stats.minhas_vendas_por_seccao?.find(v => v.seccao === seccaoNormalizada);
                        if (minhaVendaSeccao) {
                          console.log(`  📊 Minhas vendas ${seccaoNormalizada}: ${minhaVendaSeccao.total}€`);
                          minhasVendas += minhaVendaSeccao.total;
                        }
                        });
                      }
                      
                      const progressoEquipa = metaTotal > 0 ? (vendasEquipa / metaTotal) * 100 : 0;
                      const minhaContribuicao = metaTotal > 0 ? (minhasVendas / metaTotal) * 100 : 0;
                      
                      return (
                        <div key={grupo.id} className="p-4 rounded-lg bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Award className="h-5 w-5 text-purple-600" />
                              <span className="font-semibold text-lg">{grupo.nome}</span>
                            </div>
                            <div className="flex gap-2">
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                Equipa: {progressoEquipa >= 100 ? '✓' : `${progressoEquipa.toFixed(1)}%`}
                              </Badge>
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                Minha: {minhaContribuicao.toFixed(1)}%
                              </Badge>
                            </div>
                          </div>
                          <div className="text-xs text-gray-600 mb-2">
                            {grupo.secoes && Array.isArray(grupo.secoes) ? grupo.secoes.map(s => MAPEAMENTO_SECOES[s] || s).join(' + ') : 'Sem seções'}
                          </div>
                          <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                            <span>Meta: €{metaTotal.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            <span>Equipa: €{vendasEquipa.toFixed(2)} | Eu: €{minhasVendas.toFixed(2)}</span>
                          </div>
                          <Progress value={Math.min(progressoEquipa, 100)} className="h-3" />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Objetivos Mensais por Secção - DEPOIS */}
              {objetivosMensais.filter(obj => obj.valor > 0).length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Target className="h-4 w-4 text-primary" />
                    Objetivos Mensais por Secção - {MESES[mesSelecionado - 1]}
                  </h4>
                  <div className="grid gap-3">
                    {(objetivosMensais || []).filter(obj => obj.valor > 0).map((obj) => {
                      const vendaSeccao = stats.vendas_por_seccao?.find(v => v.seccao === obj.seccao);
                      const progresso = vendaSeccao ? (vendaSeccao.total / obj.valor) * 100 : 0;
                      
                      // Calcular contribuição do colaborador
                      const minhaVendaSeccao = stats.minhas_vendas_por_seccao?.find(v => v.seccao === obj.seccao);
                      const minhaContribuicao = minhaVendaSeccao ? minhaVendaSeccao.total : 0;
                      const contribuicaoPercent = obj.valor > 0 ? (minhaContribuicao / obj.valor) * 100 : 0;
                      
                      return (
                        <div key={obj.id} className="p-3 rounded-lg bg-muted/30 border border-border">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold">{obj.seccao}</span>
                            <div className="flex gap-2">
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                Equipa: {progresso >= 100 ? '✓' : `${progresso.toFixed(1)}%`}
                              </Badge>
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                Minha: {contribuicaoPercent.toFixed(1)}%
                              </Badge>
                            </div>
                          </div>
                          <div className="flex items-center justify-between text-sm text-muted-foreground mb-1">
                            <span>Meta: €{obj.valor.toLocaleString('pt-PT')}</span>
                            <span>Equipa: €{(vendaSeccao?.total || 0).toFixed(2)} | Eu: €{minhaContribuicao.toFixed(2)}</span>
                          </div>
                          <Progress value={Math.min(progresso, 100)} className="h-2" />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Objetivos Agrupados Anuais - PRIMEIRO */}
              {(() => {
                // Filtrar grupos anuais e calcular se têm metas definidas
                const gruposAnuaisComMeta = objetivosAgrupados.filter(g => {
                  if (g.tipo !== 'anual') return false;
                  
                  // Verificar se o grupo tem objetivos com valores > 0
                  let temMeta = false;
                  if (g.secoes && Array.isArray(g.secoes)) {
                    g.secoes.forEach(seccaoNome => {
                      const seccaoKey = seccaoNome.toLowerCase().replace(/ /g, '_').replace(/ã/g, 'a');
                      const seccaoNormalizada = MAPEAMENTO_SECOES[seccaoKey] || seccaoNome;
                      const objetivo = objetivos.find(o => 
                        o.tipo === 'anual' && 
                        o.seccao === seccaoNormalizada
                      );
                      if (objetivo && objetivo.valor > 0) {
                        temMeta = true;
                      }
                    });
                  }
                  return temMeta;
                });
                
                return gruposAnuaisComMeta.length > 0;
              })() && (
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Trophy className="h-4 w-4 text-green-500" />
                    Objetivos Agrupados Anuais - {anoSelecionado}
                  </h4>
                  <div className="grid gap-3">
                    {(objetivosAgrupados || []).filter(g => g.tipo === 'anual').map((grupo) => {
                      let metaTotal = 0;
                      let vendasEquipa = 0;
                      let minhasVendas = 0;
                      
                      // Somar objetivos anuais das seções incluídas
                      if (grupo.secoes && Array.isArray(grupo.secoes)) {
                        grupo.secoes.forEach(seccaoNome => {
                        // Normalizar nome da seção
                        const seccaoKey = seccaoNome.toLowerCase().replace(/ /g, '_').replace(/ã/g, 'a');
                        const seccaoNormalizada = MAPEAMENTO_SECOES[seccaoKey] || seccaoNome;
                        
                        const objetivo = objetivos.find(o => 
                          o.tipo === 'anual' && 
                          o.seccao === seccaoNormalizada
                        );
                        
                        if (objetivo) {
                          metaTotal += objetivo.valor;
                        }
                        
                        // Calcular vendas anuais da equipa
                        const vendasAnuaisSeccao = stats.vendas_anuais_por_seccao?.[seccaoNormalizada] || 0;
                        vendasEquipa += vendasAnuaisSeccao;
                        
                        // Calcular minhas vendas anuais
                        const minhasVendasAnuaisSeccao = stats.minhas_vendas_anuais_por_seccao?.[seccaoNormalizada] || 0;
                        minhasVendas += minhasVendasAnuaisSeccao;
                        });
                      }
                      
                      const progressoEquipa = metaTotal > 0 ? (vendasEquipa / metaTotal) * 100 : 0;
                      const minhaContribuicao = metaTotal > 0 ? (minhasVendas / metaTotal) * 100 : 0;
                      
                      return (
                        <div key={grupo.id} className="p-4 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Award className="h-5 w-5 text-green-600" />
                              <span className="font-semibold text-lg">{grupo.nome}</span>
                            </div>
                            <div className="flex gap-2">
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                Equipa: {progressoEquipa >= 100 ? '✓' : `${progressoEquipa.toFixed(1)}%`}
                              </Badge>
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                Minha: {minhaContribuicao.toFixed(1)}%
                              </Badge>
                            </div>
                          </div>
                          <div className="text-xs text-gray-600 mb-2">
                            {grupo.secoes && Array.isArray(grupo.secoes) ? grupo.secoes.map(s => MAPEAMENTO_SECOES[s] || s).join(' + ') : 'Sem seções'}
                          </div>
                          <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                            <span>Meta: €{metaTotal.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            <span>Equipa: €{vendasEquipa.toFixed(2)} | Eu: €{minhasVendas.toFixed(2)}</span>
                          </div>
                          <Progress value={Math.min(progressoEquipa, 100)} className="h-3" />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* OBJETIVOS ANUAIS POR SECÇÃO - V2 */}
              {objetivos && objetivos.filter(o => o.tipo === 'anual' && o.valor > 0).length > 0 ? (
                <Card className="bg-blue-50/50">
                  <CardHeader>
                    <h4 className="font-semibold flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-blue-600" />
                      🎯 Objetivos Anuais por Secção - {anoSelecionado}
                    </h4>
                  </CardHeader>
                  <CardContent>
                  <div className="grid gap-3">
                    {objetivos.filter(o => o.tipo === 'anual' && o.seccao !== 'Jogos' && o.valor > 0).map((obj) => {
                      // Usar vendas TOTAIS da monitorização (valores da equipa)
                      const vendasTotais = stats.vendas_anuais_por_seccao?.[obj.seccao] || 0;
                      console.log(`🎯 ${obj.seccao}: vendas_anuais_por_seccao = €${vendasTotais}`);
                      const progressoEquipa = (vendasTotais / obj.valor) * 100;
                      
                      // Calcular minhas vendas anuais para esta secção
                      const minhasVendasAnuais = stats.minhas_vendas_anuais_por_seccao?.[obj.seccao] || 0;
                      const minhaContribuicao = (minhasVendasAnuais / obj.valor) * 100;
                      
                      // Status de atingimento (baseado na equipa)
                      const atingido = progressoEquipa >= 100;
                      
                      return (
                        <div key={obj.id} className="p-3 rounded-lg bg-muted/30 border border-border">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold">{obj.seccao}</span>
                            <div className="flex gap-2 items-center">
                              {atingido ? (
                                <Badge variant="default" className="bg-green-500 text-white">
                                  ✓ Atingido!
                                </Badge>
                              ) : null}
                              <span className="text-xs text-muted-foreground">
                                <span className="text-blue-600 font-medium">Equipa: {progressoEquipa.toFixed(1)}%</span>
                                {" | "}
                                <span className="text-green-600 font-medium">Minha: {minhaContribuicao.toFixed(1)}%</span>
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between text-sm text-muted-foreground mb-1">
                            <span>Meta: €{obj.valor.toLocaleString('pt-PT')}</span>
                            <span>Equipa: €{vendasTotais.toFixed(2)} | Eu: €{minhasVendasAnuais.toFixed(2)}</span>
                          </div>
                          <Progress value={Math.min(progressoEquipa, 100)} className="h-2" />
                        </div>
                      );
                    })}
                  </div>
                  </CardContent>
                </Card>
              ) : null}

              {/* Objetivos Agrupados - Já estão integrados acima por tipo */}
              {false && objetivosAgrupados.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Trophy className="h-4 w-4 text-purple-500" />
                    Objetivos Agrupados - {anoSelecionado}
                  </h4>
                  <div className="grid gap-3">
                    {(objetivosAgrupados || []).map((grupo) => {
                      const mesesNomesCompletos = {
                        'janeiro': 'Jan', 'fevereiro': 'Fev', 'marco': 'Mar', 'abril': 'Abr',
                        'maio': 'Mai', 'junho': 'Jun', 'julho': 'Jul', 'agosto': 'Ago',
                        'setembro': 'Set', 'outubro': 'Out', 'novembro': 'Nov', 'dezembro': 'Dez'
                      };
                      
                      const mesesNomes = ['janeiro', 'fevereiro', 'marco', 'abril', 'maio', 'junho',
                                         'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
                      
                      // Grupos de meses específicos (TRIMESTRAIS)
                      if (grupo.tipo === 'meses_especificos' && grupo.mesesSelecionados && grupo.mesesAgrupados) {
                        let metaTotal = 0;
                        let vendasEquipa = 0;
                        let minhasVendas = 0;
                        
                        // Carregar vendas anuais do estado para cálculos
                        const vendasAnuaisList = stats.vendas_anuais_por_seccao || {};
                        
                        // Calcular objetivos para cada mês selecionado e cada seção
                        if (grupo.mesesSelecionados && Array.isArray(grupo.mesesSelecionados)) {
                          grupo.mesesSelecionados.forEach(mesNome => {
                            const mesIdx = mesesNomes.indexOf(mesNome);
                            
                            if (grupo.secoes && Array.isArray(grupo.secoes)) {
                              grupo.secoes.forEach(seccaoNome => {
                            // Encontrar o objetivo para esta seção e mês
                            const seccaoNormalizada = MAPEAMENTO_SECOES[seccaoNome.toLowerCase().replace(/ /g, '_').replace(/ã/g, 'a')] || seccaoNome;
                            const objetivo = objetivos.find(o => 
                              o.tipo === 'mensal' && 
                              o.seccao === seccaoNormalizada && 
                              o.mes === mesIdx + 1
                            );
                            
                            if (objetivo) {
                              metaTotal += objetivo.valor;
                            }
                              });
                            }
                          });
                        }
                        
                        // Para vendas, precisamos das vendas anuais filtradas por mês
                        // Como não temos acesso direto às vendas brutas aqui, vamos usar as vendas agregadas
                        if (grupo.secoes && Array.isArray(grupo.secoes)) {
                          grupo.secoes.forEach(seccaoNome => {
                          const seccaoNormalizada = MAPEAMENTO_SECOES[seccaoNome.toLowerCase().replace(/ /g, '_').replace(/ã/g, 'a')] || seccaoNome;
                          vendasEquipa += vendasAnuaisList[seccaoNormalizada] || 0;
                          
                          // Minhas vendas anuais
                          const minhasVendasSeccao = stats.minhas_vendas_anuais_por_seccao?.[seccaoNormalizada] || 0;
                          minhasVendas += minhasVendasSeccao;
                          });
                        }
                        
                        const progressoEquipa = metaTotal > 0 ? (vendasEquipa / metaTotal) * 100 : 0;
                        const minhaContribuicao = metaTotal > 0 ? (minhasVendas / metaTotal) * 100 : 0;
                        const mesesStr = grupo.mesesSelecionados && Array.isArray(grupo.mesesSelecionados) 
                          ? grupo.mesesSelecionados.map(m => mesesNomesCompletos[m] || m).join('-')
                          : 'Sem meses';
                        
                        return (
                          <div key={grupo.id} className="p-4 rounded-lg bg-gradient-card border border-border">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Award className="h-5 w-5 text-cyan-500" />
                                <span className="font-semibold text-lg">{grupo.nome}</span>
                                <span className="text-xs px-2 py-1 rounded bg-cyan-100 text-cyan-700">
                                  📊 {mesesStr}
                                </span>
                              </div>
                              <div className="flex gap-2">
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                  Equipa: {progressoEquipa >= 100 ? '✓' : `${progressoEquipa.toFixed(1)}%`}
                                </Badge>
                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                  Minha: {minhaContribuicao.toFixed(1)}%
                                </Badge>
                              </div>
                            </div>
                            <div className="text-xs text-gray-600 mb-2">
                              {grupo.secoes && Array.isArray(grupo.secoes) ? grupo.secoes.map(s => MAPEAMENTO_SECOES[s] || s).join(' + ') : 'Sem seções'}
                            </div>
                            <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                              <span>Meta: €{metaTotal.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              <span>Equipa: €{vendasEquipa.toFixed(2)} | Eu: €{minhasVendas.toFixed(2)}</span>
                            </div>
                            <Progress value={Math.min(progressoEquipa, 100)} className="h-3" />
                          </div>
                        );
                      }
                      
                      // GRUPOS MENSAIS - mostrar apenas o mês atual
                      else if (grupo.tipo === 'mensal') {
                        let metaTotal = 0;
                        let vendasEquipa = 0;
                        let minhasVendas = 0;
                        
                        // Somar apenas o mês selecionado (mês atual)
                        if (grupo.secoes && Array.isArray(grupo.secoes)) {
                          grupo.secoes.forEach(seccaoNome => {
                          // Normalizar nome da seção
                          const seccaoKey = seccaoNome.toLowerCase().replace(/ /g, '_').replace(/ã/g, 'a');
                          const seccaoNormalizada = MAPEAMENTO_SECOES[seccaoKey] || seccaoNome;
                          
                          const objetivo = objetivos.find(o => 
                            o.tipo === 'mensal' && 
                            o.seccao === seccaoNormalizada && 
                            o.mes === mesSelecionado
                          );
                          
                          if (objetivo) {
                            metaTotal += objetivo.valor;
                          }
                          
                          // Calcular vendas da equipa (todas as vendas do mês atual desta secção)
                          const vendaSeccao = stats.vendas_por_seccao.find(v => v.seccao === seccaoNormalizada);
                          if (vendaSeccao) {
                            vendasEquipa += vendaSeccao.total;
                          }
                          
                          // Calcular minhas vendas (só do colaborador logado)
                          const minhaVendaSeccao = stats.minhas_vendas_por_seccao?.find(v => v.seccao === seccaoNormalizada);
                          if (minhaVendaSeccao) {
                            minhasVendas += minhaVendaSeccao.total;
                          }
                          });
                        }
                        
                        const progressoEquipa = metaTotal > 0 ? (vendasEquipa / metaTotal) * 100 : 0;
                        const minhaContribuicao = metaTotal > 0 ? (minhasVendas / metaTotal) * 100 : 0;
                        const mesNome = MESES[mesSelecionado - 1];
                        
                        return (
                          <div key={grupo.id} className="p-4 rounded-lg bg-gradient-card border border-border">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Target className="h-5 w-5 text-purple-500" />
                                <span className="font-semibold text-lg">{grupo.nome}</span>
                                <span className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-700">
                                  📅 {mesNome}
                                </span>
                              </div>
                              <div className="flex gap-2">
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                  Equipa: {progressoEquipa >= 100 ? '✓' : `${progressoEquipa.toFixed(1)}%`}
                                </Badge>
                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                  Minha: {minhaContribuicao.toFixed(1)}%
                                </Badge>
                              </div>
                            </div>
                            <div className="text-xs text-gray-600 mb-2">
                              {grupo.secoes && Array.isArray(grupo.secoes) ? grupo.secoes.map(s => MAPEAMENTO_SECOES[s] || s).join(' + ') : 'Sem seções'}
                            </div>
                            <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                              <span>Meta: €{metaTotal.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              <span>Equipa: €{vendasEquipa.toFixed(2)} | Eu: €{minhasVendas.toFixed(2)}</span>
                            </div>
                            <Progress value={Math.min(progressoEquipa, 100)} className="h-3" />
                          </div>
                        );
                      }
                      
                      // Grupos ANUAIS - sempre exibir
                      else if (grupo.tipo === 'anual') {
                        let metaTotal = 0;
                        let vendasEquipa = 0;
                        let minhasVendas = 0;
                        
                        // Somar objetivos anuais das seções incluídas
                        if (grupo.secoes && Array.isArray(grupo.secoes)) {
                          grupo.secoes.forEach(seccaoNome => {
                          // Normalizar nome da seção
                          const seccaoKey = seccaoNome.toLowerCase().replace(/ /g, '_').replace(/ã/g, 'a');
                          const seccaoNormalizada = MAPEAMENTO_SECOES[seccaoKey] || seccaoNome;
                          
                          console.log(`  - Processando: ${seccaoNome} -> ${seccaoKey} -> ${seccaoNormalizada}`);
                          
                          const objetivo = objetivos.find(o => 
                            o.tipo === 'anual' && 
                            o.seccao === seccaoNormalizada
                          );
                          
                          if (objetivo) {
                            console.log(`  ✅ Objetivo encontrado: ${objetivo.valor}€`);
                            metaTotal += objetivo.valor;
                          } else {
                            console.log(`  ❌ Objetivo NÃO encontrado para: ${seccaoNormalizada}`);
                          }
                          
                          // Calcular vendas anuais da equipa
                          const vendasAnuaisSeccao = stats.vendas_anuais_por_seccao?.[seccaoNormalizada] || 0;
                          console.log(`  📊 Vendas equipa ${seccaoNormalizada}: ${vendasAnuaisSeccao}€`);
                          vendasEquipa += vendasAnuaisSeccao;
                          
                          // Calcular minhas vendas anuais
                          const minhasVendasAnuaisSeccao = stats.minhas_vendas_anuais_por_seccao?.[seccaoNormalizada] || 0;
                          console.log(`  📊 Minhas vendas ${seccaoNormalizada}: ${minhasVendasAnuaisSeccao}€`);
                          minhasVendas += minhasVendasAnuaisSeccao;
                          });
                        }
                        
                        const progressoEquipa = metaTotal > 0 ? (vendasEquipa / metaTotal) * 100 : 0;
                        const minhaContribuicao = metaTotal > 0 ? (minhasVendas / metaTotal) * 100 : 0;
                        
                        return (
                          <div key={grupo.id} className="p-4 rounded-lg bg-gradient-card border border-border">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Award className="h-5 w-5 text-green-500" />
                                <span className="font-semibold text-lg">{grupo.nome}</span>
                                <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700">
                                  📆 Anual
                                </span>
                              </div>
                              <div className="flex gap-2">
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                  Equipa: {progressoEquipa >= 100 ? '✓' : `${progressoEquipa.toFixed(1)}%`}
                                </Badge>
                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                  Minha: {minhaContribuicao.toFixed(1)}%
                                </Badge>
                              </div>
                            </div>
                            <div className="text-xs text-gray-600 mb-2">
                              {grupo.secoes && Array.isArray(grupo.secoes) ? grupo.secoes.map(s => MAPEAMENTO_SECOES[s] || s).join(' + ') : 'Sem seções'}
                            </div>
                            <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                              <span>Meta: €{metaTotal.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              <span>Equipa: €{vendasEquipa.toFixed(2)} | Eu: €{minhasVendas.toFixed(2)}</span>
                            </div>
                            <Progress value={Math.min(progressoEquipa, 100)} className="h-3" />
                          </div>
                        );
                      }
                      
                      return null;
                    })}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Objetivos Personalizados Ativos (no final, depois de tudo) */}
      <ObjetivosPersonalizadosAtivosColab />
    </div>
  );
};

// Componente para Objetivos Personalizados Ativos (Colaboradores)
const ObjetivosPersonalizadosAtivosColab = () => {
  const [periodosAtivos, setPeriodosAtivos] = useState([]);
  const [vendasEquipa, setVendasEquipa] = useState({});
  const [minhasVendas, setMinhasVendas] = useState({});
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);

  const MAPEAMENTO_SECOES = {
    'cafe': 'Café',
    'loja_avelal': 'Loja Avelal',
    'loja_satao': 'Loja Sátão',
    'escritorio': 'Escritório',
    'raspadinhas': 'Raspadinhas',
    'restaurante': 'Restaurante',
    'jogos': 'Jogos'
  };

  useEffect(() => {
    // Buscar dados do usuário
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setUser(JSON.parse(userStr));
    }
    carregarObjetivosAtivos();
  }, []);

  const carregarObjetivosAtivos = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Buscar períodos ativos
      const response = await fetch(`${API_URL}/api/objetivos-personalizados/ativos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const periodos = await response.json();
      
      if (periodos.length === 0) {
        setPeriodosAtivos([]);
        return;
      }
      
      // Para cada período, buscar vendas da equipa e minhas vendas
      const vendasEquipaPorPeriodo = {};
      const minhasVendasPorPeriodo = {};
      
      for (const periodo of periodos) {
        const hoje = new Date().toISOString().split('T')[0];
        const dataInicio = periodo.data_inicio;
        const dataFim = hoje < periodo.data_fim ? hoje : periodo.data_fim;
        
        // Vendas da equipa
        const vendasEquipaResp = await fetch(
          `${API_URL}/api/vendas/totais-por-secao-periodo?data_inicio=${dataInicio}&data_fim=${dataFim}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        vendasEquipaPorPeriodo[periodo.id] = await vendasEquipaResp.json();
        
        // Minhas vendas (endpoint novo que filtra por colaborador)
        const minhasVendasResp = await fetch(
          `${API_URL}/api/vendas/minhas-vendas-por-secao-periodo?data_inicio=${dataInicio}&data_fim=${dataFim}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        minhasVendasPorPeriodo[periodo.id] = await minhasVendasResp.json();
      }
      
      setPeriodosAtivos(periodos);
      setVendasEquipa(vendasEquipaPorPeriodo);
      setMinhasVendas(minhasVendasPorPeriodo);
    } catch (error) {
      console.error('Erro ao carregar objetivos personalizados ativos:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || periodosAtivos.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="h-5 w-5 text-purple-500" />
          🎯 Objetivos Personalizados Ativos
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Acompanhe o progresso da equipa e a sua contribuição individual
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {periodosAtivos.map(periodo => {
            const vendasEq = vendasEquipa[periodo.id] || {};
            const minhasVend = minhasVendas[periodo.id] || {};
            const secoes = periodo.secoes || {};
            const grupos = periodo.grupos || [];
            
            return (
              <div key={periodo.id} className="space-y-4">
                {/* Cabeçalho do Período */}
                <div className="p-4 rounded-lg bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-xl text-purple-700">{periodo.nome}</h3>
                      <p className="text-sm text-purple-600 mt-1">
                        📅 {new Date(periodo.data_inicio).toLocaleDateString('pt-PT')} até{' '}
                        {new Date(periodo.data_fim).toLocaleDateString('pt-PT')}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Objetivos Agrupados (se houver) */}
                {grupos.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm text-muted-foreground flex items-center gap-2">
                      <Trophy className="h-4 w-4 text-purple-500" />
                      Objetivos Agrupados
                    </h4>
                    {grupos.map(grupo => {
                      let metaTotal = 0;
                      let vendasEquipaTotal = 0;
                      let minhasVendasTotal = 0;
                      
                      // Calcular totais do grupo
                      (grupo.secoes || []).forEach(secaoKey => {
                        const objetivo = secoes[secaoKey]?.objetivo || 0;
                        metaTotal += objetivo;
                        vendasEquipaTotal += vendasEq[secaoKey] || 0;
                        minhasVendasTotal += minhasVend[secaoKey] || 0;
                      });
                      
                      const progressoEquipa = metaTotal > 0 ? (vendasEquipaTotal / metaTotal) * 100 : 0;
                      const minhaContribuicao = metaTotal > 0 ? (minhasVendasTotal / metaTotal) * 100 : 0;
                      
                      return (
                        <div key={grupo.id} className="p-4 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Award className="h-5 w-5 text-green-600" />
                              <span className="font-semibold text-lg">{grupo.nome}</span>
                            </div>
                            <div className="flex gap-2">
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                Equipa: {progressoEquipa >= 100 ? '✓' : `${progressoEquipa.toFixed(1)}%`}
                              </Badge>
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                Minha: {minhaContribuicao.toFixed(1)}%
                              </Badge>
                            </div>
                          </div>
                          <div className="text-xs text-gray-600 mb-2">
                            {grupo.secoes.map(s => MAPEAMENTO_SECOES[s] || s).join(' + ')}
                          </div>
                          <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                            <span>Meta: €{metaTotal.toLocaleString('pt-PT', { minimumFractionDigits: 2 })}</span>
                            <span>Equipa: €{vendasEquipaTotal.toFixed(2)} | Eu: €{minhasVendasTotal.toFixed(2)}</span>
                          </div>
                          <Progress value={Math.min(progressoEquipa, 100)} className="h-3" />
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Objetivos Individuais por Secção */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm text-muted-foreground flex items-center gap-2">
                    <Target className="h-4 w-4 text-blue-500" />
                    Objetivos por Secção
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {Object.entries(secoes).map(([secaoId, dados]) => {
                      const objetivo = dados.objetivo || 0;
                      if (objetivo === 0) return null;
                      
                      const vendaEquipa = vendasEq[secaoId] || 0;
                      const minhaVenda = minhasVend[secaoId] || 0;
                      const progressoEquipa = objetivo > 0 ? (vendaEquipa / objetivo) * 100 : 0;
                      const progressoMinha = objetivo > 0 ? (minhaVenda / objetivo) * 100 : 0;
                      const secaoNome = MAPEAMENTO_SECOES[secaoId] || secaoId;
                      
                      return (
                        <div key={secaoId} className="p-3 rounded-lg bg-white border border-border hover:shadow-md transition-shadow">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-sm">{secaoNome}</span>
                          </div>
                          <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                            <span>Meta: €{objetivo.toLocaleString('pt-PT')}</span>
                          </div>
                          <div className="space-y-1">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-blue-600 font-medium">Equipa: {progressoEquipa.toFixed(1)}%</span>
                              <span className="text-blue-600">€{vendaEquipa.toFixed(2)}</span>
                            </div>
                            <Progress value={Math.min(progressoEquipa, 100)} className="h-2 bg-blue-100" />
                            <div className="flex items-center justify-between text-xs mt-1">
                              <span className="text-green-600 font-medium">Minha: {progressoMinha.toFixed(1)}%</span>
                              <span className="text-green-600">€{minhaVenda.toFixed(2)}</span>
                            </div>
                            <Progress value={Math.min(progressoMinha, 100)} className="h-2 bg-green-100" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default DashboardTab;
